import { api } from './api';

export interface HeadcountPlan {
  id: string;
  workspaceId: string;
  entityId: string | null;
  role: string;
  department: string | null;
  location: string | null;
  fte: number;
  salaryBandLow: number | null;
  salaryBandMid: number;
  salaryBandHigh: number | null;
  currency: string;
  onCostsPct: number;
  startDate: string;
  endDate: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHeadcountPlanBody {
  role: string;
  department?: string | null;
  location?: string | null;
  fte: number;
  salaryBandLow?: number | null;
  salaryBandMid: number;
  salaryBandHigh?: number | null;
  currency: string;
  onCostsPct?: number;
  startDate: string;
  endDate?: string | null;
  notes?: string | null;
  entityId?: string | null;
}

export type UpdateHeadcountPlanBody = Partial<CreateHeadcountPlanBody>;

export interface RolloutLine {
  month: string;
  planId: string;
  role: string;
  salaryCost: number;
  onCosts: number;
  total: number;
  currency: string;
}

export interface RolloutResult {
  periodStart: string;
  periodEnd: string;
  grandTotal: number;
  totalByCurrency: { currency: string; total: number }[];
  totalByMonth: { month: string; total: number }[];
  lines: RolloutLine[];
}

export const headcountApi = {
  async list(workspaceId: string): Promise<HeadcountPlan[]> {
    const r = await api.get<HeadcountPlan[]>(`/api/workspaces/${workspaceId}/headcount-plans`);
    return r.data;
  },
  async create(workspaceId: string, body: CreateHeadcountPlanBody): Promise<HeadcountPlan> {
    const r = await api.post<HeadcountPlan>(`/api/workspaces/${workspaceId}/headcount-plans`, body);
    return r.data;
  },
  async update(id: string, body: UpdateHeadcountPlanBody): Promise<HeadcountPlan> {
    const r = await api.patch<HeadcountPlan>(`/api/headcount-plans/${id}`, body);
    return r.data;
  },
  async delete(id: string): Promise<void> {
    await api.delete(`/api/headcount-plans/${id}`);
  },
  async rollout(workspaceId: string, q: { periodStart: string; periodEnd: string }): Promise<RolloutResult> {
    const r = await api.get<RolloutResult>(
      `/api/workspaces/${workspaceId}/headcount-rollout`,
      { params: q },
    );
    return r.data;
  },
  /** Project planned people-cost into fp_facts as scenario 'plan:headcount' (Growth+). Idempotent. */
  async project(workspaceId: string, body: { periodStart: string; periodEnd: string }): Promise<ProjectionResult> {
    const r = await api.post<ProjectionResult>(`/api/workspaces/${workspaceId}/headcount-plans/project`, body);
    return r.data;
  },
};

export interface ProjectionResult {
  scenario: string;
  account: string;
  factsInserted: number;
  periodStart: string;
  periodEnd: string;
}
